import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  IconButton,
  TextField,
  InputAdornment,
  Typography,
  Box,
} from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import useProfile from 'hooks/useProfile';
import { useTranslation } from 'next-i18next';
import { useCallback, useRef, useState, useEffect } from 'react';
import type { FC } from 'react';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';

import { useAppDispatch, useAppSelector } from 'redux-store';
import addDeviceThunk from 'redux-store/thunks/devices/addDeviceThunk';
import getAllDevicesThunk from 'redux-store/thunks/devices/getAllDevicesThunk';

import type { AddDeviceGroupModalStageProps } from '..';

export interface AddNewDeviceContentProps
  extends AddDeviceGroupModalStageProps {
  groupId?: number;
}

const AddNewDeviceContent: FC<AddNewDeviceContentProps> = ({
  open,
  groupId,
  setState,
}) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const profile = useProfile();

  const serials = useAppSelector(status =>
    status.devices.items.map(device => device.serial),
  );

  const [serialNumber, setSerialNumber] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const serialInputRef = useRef<HTMLInputElement>(null);

  const handleAddDevice = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();

      if (serials.includes(serialNumber)) {
        setError(t('common:api.device_already_exists'));

        return;
      }

      if (isLoading || serialNumber.length < 6 || !password.length) {
        return;
      }

      try {
        setLoading(true);

        const result = await dispatch(
          addDeviceThunk({ serial: serialNumber, password }),
        ).unwrap();

        if (result.success) {
          if (profile) {
            await dispatch(getAllDevicesThunk({ userId: profile.id }));
          }

          setState(prevState => ({
            ...prevState,
            devices: [...prevState.devices, serialNumber],
            stage: 'devices',
          }));

          setSerialNumber('');
          setPassword('');
        } else {
          const errorMsg = i18n.exists(result.error)
            ? t(result.error)
            : result.error;
          setError(errorMsg);
        }
      } catch (e) {
        Sentry.captureException(e, {
          extra: { serialNumber },
          tags: { page: 'AddNewDeviceContentModal' },
        });
      } finally {
        setLoading(false);
      }
    },
    [
      isLoading,
      serialNumber,
      password,
      serials,
      t,
      dispatch,
      profile,
      setState,
      i18n,
    ],
  );

  useEffect(() => {
    if (open) {
      serialInputRef.current?.focus();
    }
  }, [open]);

  const validSerialNumber =
    serialNumber.length >= 6 && serialNumber.length <= 10;
  const validPassword = password.length > 0;
  const valid = validSerialNumber && validPassword && !error;

  const handleNavigateToInstructions = (): void => {
    setState(prev => ({
      ...prev,
      stage: 'instructions',
    }));
  };

  return (
    <Box height="100%" sx={{ overflowY: 'auto' }}>
      <form onSubmit={handleAddDevice}>
        <Box mb={3}>
          <Typography
            variant="h2"
            textAlign="center"
            data-testid="new-device-title"
            mb={2}
          >
            {t('common:add_new_device')}
          </Typography>
          <Typography variant="body1" textAlign="center">
            {t('common:add_new_device_description')}
            {typeof groupId === 'number'
              ? t('common:add_new_device_device_group_description')
              : null}
          </Typography>
          <Typography
            textAlign="center"
            variant="body1"
            color="primary"
            sx={{ cursor: 'pointer' }}
            onClick={handleNavigateToInstructions}
          >
            {t('common:add_device_info.title')}
          </Typography>
        </Box>
        <Box mb={2}>
          <TextField
            label={t('common:serial_number')}
            name="serial"
            value={serialNumber}
            onChange={(e): void => {
              setError(null);
              setSerialNumber(e.target.value);
            }}
            type="text"
            autoComplete="off"
            fullWidth
            inputProps={{
              maxLength: 10,
              'data-testid': 'new-device-serial',
            }}
            variant="filled"
            disabled={isLoading}
            inputRef={serialInputRef}
          />
        </Box>
        <Box mb={4}>
          <TextField
            label={t('common:password')}
            name="password"
            value={password}
            onChange={(e): void => {
              setError(null);
              setPassword(e.target.value);
            }}
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={(): void => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
              inputProps: { 'data-testid': 'new-device-password' },
            }}
            fullWidth
            variant="filled"
            disabled={isLoading}
          />
        </Box>
        <Box mb={2} pt={2}>
          <Button
            fullWidth
            variant="flat"
            type="submit"
            disabled={!valid || isLoading}
            size="large"
            data-testid="submit"
          >
            {t('common:add')}
          </Button>
        </Box>
        <ErrorText error={error} />
      </form>
    </Box>
  );
};

export default AddNewDeviceContent;
